import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Check, ChevronLeft, ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August",
  "September", "Oktober", "November", "Dezember"];

const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");
const FIELD = "w-full bg-white border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]";

const Calendar = ({ blocked, weekdays, onToggleDay }) => {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const todayIso = iso(now.getFullYear(), now.getMonth(), now.getDate());
  const first = new Date(cursor.y, cursor.m, 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const shift = (delta) => {
    const d = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };
  return (
    <div data-testid="portal-calendar">
      <div className="flex items-center justify-between mb-4">
        <button type="button" onClick={() => shift(-1)} data-testid="cal-prev"
          className="p-2 border border-[#E3DECE] hover:border-[#BA6D52] transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <p className="font-serif text-xl">{MONTHS[cursor.m]} {cursor.y}</p>
        <button type="button" onClick={() => shift(1)} data-testid="cal-next"
          className="p-2 border border-[#E3DECE] hover:border-[#BA6D52] transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, i) => (
          <div key={w} className={`text-[10px] uppercase tracking-[0.12em] pb-2 ${weekdays.includes(i) ? "text-[#BA6D52]" : "text-[#757067]"}`}>{w}</div>
        ))}
        {Array.from({ length: offset }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const day = iso(cursor.y, cursor.m, i + 1);
          const past = day < todayIso;
          const wd = (new Date(cursor.y, cursor.m, i + 1).getDay() + 6) % 7;
          const byWeekday = weekdays.includes(wd);
          const isBlocked = blocked.includes(day) || byWeekday;
          return (
            <button key={day} type="button" disabled={past || byWeekday}
              onClick={() => onToggleDay(day)} data-testid={`cal-day-${day}`}
              className={`aspect-square text-sm transition-colors border ${
                past ? "text-[#CFC9BE] border-transparent cursor-not-allowed"
                : isBlocked ? "bg-[#2C2A26] text-white border-[#2C2A26]"
                : "bg-white border-[#E3DECE] text-[#2C2A26] hover:border-[#BA6D52]"}`}>
              {i + 1}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-[#757067] mt-4 leading-relaxed">
        Alle Tage gelten als <b className="text-[#2C2A26]">frei</b>. Tippe die Tage an, an denen du
        <b className="text-[#2C2A26]"> nicht</b> kannst — dunkel = belegt.
      </p>
    </div>
  );
};

const JobCard = ({ job, onAnswer }) => {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(job.tagessatz_eur || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (response) => {
    setBusy(true);
    await onAnswer(job.job_id, { response, tagessatz_eur: rate ? Number(rate) : null, note });
    setBusy(false);
  };
  return (
    <div className="bg-white border border-[#E3DECE] p-6" data-testid={`portal-job-${job.job_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Einsatz {job.job_number}</p>
          <h3 className="font-serif text-2xl mt-2">{job.betrieb}</h3>
          <p className="text-sm text-[#757067] mt-1">{job.plz} {job.ort}</p>
        </div>
        {job.tagessatz_eur ? (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#757067]">Tagessatz</p>
            <p className="font-serif text-2xl">{job.tagessatz_eur.toFixed(2)} €</p>
            <p className="text-[11px] text-[#757067]">netto</p>
          </div>
        ) : null}
      </div>
      <div className="mt-5 pt-5 border-t border-[#E3DECE] grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        {[["Einsatztage", job.dates.map(fmt).join(" · ")],
          ["Tageszeit", job.taeglich_von ? `${job.taeglich_von} – ${job.taeglich_bis}` : "nach Absprache"],
          ["Position", job.position || "—"],
          ["Bereiche", job.bereiche || "—"]].map(([k, v]) => (
          <div key={k} className="flex gap-3">
            <span className="text-[#757067] shrink-0 min-w-[110px]">{k}</span>
            <span className="text-[#2C2A26]">{v}</span>
          </div>
        ))}
      </div>
      {job.beschreibung && (
        <p className="text-sm text-[#575349] mt-4 pt-4 border-t border-[#E3DECE] whitespace-pre-line leading-relaxed">
          {job.beschreibung}
        </p>
      )}
      {job.response ? (
        <p className={`mt-5 text-sm ${job.response === "yes" ? "text-green-800" : "text-[#757067]"}`}>
          {job.response === "yes"
            ? (job.selected ? "Du bist eingeteilt ✓" : "Zugesagt — ich melde mich, sobald der Betrieb entschieden hat.")
            : "Abgesagt."}
        </p>
      ) : (
        <>
          {open && (
            <div className="mt-5 pt-5 border-t border-[#E3DECE] grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2">
                  Dein Tagessatz (€ netto, optional)
                </label>
                <input type="number" step="10" value={rate} onChange={(e) => setRate(e.target.value)}
                  className={FIELD} data-testid={`portal-job-rate-${job.job_id}`} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2">
                  Anmerkung (optional)
                </label>
                <input value={note} onChange={(e) => setNote(e.target.value)} className={FIELD}
                  placeholder="z. B. nur Fr + Sa möglich" data-testid={`portal-job-note-${job.job_id}`} />
              </div>
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" disabled={busy} onClick={() => send("yes")}
              data-testid={`portal-job-yes-${job.job_id}`}
              className="inline-flex items-center gap-2 bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-7 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors">
              <Check className="w-4 h-4" /> Zusagen
            </button>
            <button type="button" disabled={busy} onClick={() => send("no")}
              data-testid={`portal-job-no-${job.job_id}`}
              className="inline-flex items-center gap-2 border border-[#E3DECE] text-[#757067] hover:border-[#2C2A26] hover:text-[#2C2A26] disabled:opacity-60 px-6 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors">
              <X className="w-4 h-4" /> Passt nicht
            </button>
            {!open && (
              <button type="button" onClick={() => setOpen(true)} data-testid={`portal-job-more-${job.job_id}`}
                className="text-[11px] uppercase tracking-[0.2em] text-[#BA6D52] hover:text-[#9E5841]">
                Tagessatz / Anmerkung angeben
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default function KochPortal() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [quick, setQuick] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vermittlung/koch/portal/${id}?token=${token}`);
      setData(r.data);
      setBlocked(r.data.blocked_dates || []);
      setWeekdays(r.data.blocked_weekdays || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Dieser Link ist nicht mehr gültig.");
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  // Ein-Klick-Antwort direkt aus der Einladungs-Mail (?a=yes|no&job=…)
  useEffect(() => {
    const a = params.get("a");
    const jobId = params.get("job");
    if (!a || !jobId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/vermittlung/koch/portal/${id}/jobs/${jobId}?token=${token}`,
          { response: a === "yes" ? "yes" : "no" });
        if (!cancelled) setQuick(a === "yes" ? "yes" : "no");
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || "Antwort konnte nicht gespeichert werden");
      } finally {
        if (!cancelled) {
          setParams({ token }, { replace: true });
          load();
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const dirty = useMemo(() => {
    if (!data) return false;
    const a = JSON.stringify([...blocked].sort()) !== JSON.stringify([...(data.blocked_dates || [])].sort());
    const b = JSON.stringify([...weekdays].sort()) !== JSON.stringify([...(data.blocked_weekdays || [])].sort());
    return a || b;
  }, [blocked, weekdays, data]);

  const toggleDay = (day) =>
    setBlocked((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));

  const toggleWeekday = (i) =>
    setWeekdays((prev) => (prev.includes(i) ? prev.filter((w) => w !== i) : [...prev, i]));

  const saveAvailability = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/vermittlung/koch/portal/${id}/verfuegbarkeit?token=${token}`,
        { blocked_dates: blocked, blocked_weekdays: weekdays });
      toast.success("Verfügbarkeit gespeichert");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const answer = async (jobId, body) => {
    try {
      await axios.post(`${API}/vermittlung/koch/portal/${id}/jobs/${jobId}?token=${token}`, body);
      toast.success(body.response === "yes" ? "Zusage ist raus — danke!" : "Absage notiert");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Antwort konnte nicht gespeichert werden");
    }
  };

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 md:py-32" data-testid="portal-error">
        <h1 className="font-serif text-3xl font-light tracking-tight">Link nicht gültig</h1>
        <p className="text-[#575349] mt-5 leading-relaxed">{error}</p>
        <p className="text-sm text-[#757067] mt-5">Melde dich gerne direkt: +49 151 27282560</p>
      </div>
    );
  }
  if (!data) return <div className="max-w-xl mx-auto px-6 py-32 text-sm text-[#757067]">Lade …</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20" data-testid="koch-portal">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">Mein Koch-Bereich</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-5">
        Servus {data.vorname}.
      </h1>
      <p className="text-[#757067] font-light text-lg mt-5 leading-relaxed max-w-2xl">
        Hier siehst du deine Einsatz-Anfragen und pflegst deine freien Tage — ohne Anmeldung,
        ohne Passwort. Lesezeichen setzen und jederzeit wieder aufrufen.
      </p>
      <p className="text-xs text-[#757067] mt-4">
        {data.chef_number} · Einsatzgebiet {data.plz} {data.ort} · {data.umkreis_km} km Umkreis
        {data.availability_updated_at ? ` · Verfügbarkeit aktualisiert ${fmt(data.availability_updated_at)}` : ""}
      </p>

      {quick && (
        <div className={`mt-8 p-5 border-l-4 ${quick === "yes" ? "bg-[#FAF5F1] border-[#BA6D52]" : "bg-[#F2EFE9] border-[#757067]"}`}
          data-testid="portal-quick-answer">
          <p className="font-serif text-xl text-[#2C2A26]">
            {quick === "yes" ? "Deine Zusage ist raus — danke!" : "Alles klar, ist notiert."}
          </p>
          <p className="text-sm text-[#575349] mt-2 leading-relaxed">
            {quick === "yes"
              ? "Ich melde mich, sobald der Betrieb entschieden hat. Wenn du noch einen eigenen Tagessatz oder eine Anmerkung nennen möchtest, ruf mich einfach kurz an."
              : "Diesen Einsatz gebe ich weiter. Trag gerne unten deine belegten Tage ein, dann passen meine nächsten Anfragen besser."}
          </p>
        </div>
      )}

      <section className="mt-14">
        <h2 className="font-serif text-2xl md:text-3xl font-light">
          Offene Anfragen
          {data.offene_anfragen.length > 0 && (
            <span className="ml-3 align-middle text-[11px] uppercase tracking-[0.15em] bg-[#BA6D52] text-white px-2 py-1">
              {data.offene_anfragen.length} neu
            </span>
          )}
        </h2>
        <div className="mt-6 space-y-5">
          {data.offene_anfragen.length === 0 ? (
            <p className="text-sm text-[#757067]" data-testid="portal-no-jobs">
              Gerade keine offene Anfrage. Sobald ein Einsatz in deinem Umkreis reinkommt, bekommst
              du eine E-Mail — und hier kannst du direkt zusagen.
            </p>
          ) : (
            data.offene_anfragen.map((j) => <JobCard key={j.job_id} job={j} onAnswer={answer} />)
          )}
        </div>
      </section>

      <section className="mt-16 bg-[#F2EFE9] p-6 md:p-8">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-5 h-5 text-[#BA6D52]" strokeWidth={1.5} />
          <h2 className="font-serif text-2xl font-light">Meine freien Tage</h2>
        </div>
        <p className="text-sm text-[#575349] mt-3 leading-relaxed">
          Je aktueller das hier ist, desto passender sind meine Anfragen — du bekommst dann keine
          Einsätze mehr für Tage, an denen du sowieso nicht kannst.
        </p>
        <div className="mt-7 grid md:grid-cols-[1fr_auto] gap-8">
          <Calendar blocked={blocked} weekdays={weekdays} onToggleDay={toggleDay} />
          <div className="md:w-56">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-3">
              Dauerhaft nie verfügbar
            </p>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((w, i) => (
                <button key={w} type="button" onClick={() => toggleWeekday(i)}
                  data-testid={`portal-weekday-${i}`}
                  className={`px-3 py-2 text-xs border transition-colors ${
                    weekdays.includes(i) ? "bg-[#2C2A26] text-white border-[#2C2A26]"
                      : "bg-white border-[#E3DECE] hover:border-[#BA6D52]"}`}>
                  {w}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#757067] mt-3 leading-relaxed">
              Praktisch, wenn du z. B. montags immer in deinem Stammbetrieb bist.
            </p>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button type="button" onClick={saveAvailability} disabled={!dirty || saving}
            data-testid="portal-save-availability"
            className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-50 px-8 py-4 text-[11px] uppercase tracking-[0.2em] transition-colors">
            {saving ? "Speichert …" : "Verfügbarkeit speichern"}
          </button>
          {dirty && <span className="text-xs text-[#BA6D52]">Nicht gespeicherte Änderungen</span>}
        </div>
      </section>

      {data.vergangene_anfragen.length > 0 && (
        <section className="mt-16">
          <h2 className="font-serif text-2xl font-light">Bisherige Anfragen</h2>
          <div className="mt-5 divide-y divide-[#E3DECE] border-t border-[#E3DECE]">
            {data.vergangene_anfragen.map((j) => (
              <div key={j.job_id} className="py-4 flex flex-wrap items-center justify-between gap-3 text-sm"
                data-testid={`portal-past-${j.job_id}`}>
                <span className="text-[#2C2A26]">{j.betrieb} · {j.dates.map(fmt).join(" · ")}</span>
                <span className={j.selected ? "text-green-800" : "text-[#757067]"}>
                  {j.selected ? "eingeteilt ✓" : j.response === "yes" ? "zugesagt" : j.response === "no" ? "abgesagt" : "nicht mehr aktuell"}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="text-xs text-[#757067] mt-16 leading-relaxed border-t border-[#E3DECE] pt-6">
        Für dich ist die Vermittlung dauerhaft kostenlos — die Provision zahlt immer der Betrieb.
        Deine Leistung rechnest du direkt mit dem Betrieb ab. Fragen? Einfach anrufen:
        +49 151 27282560.
      </p>
    </div>
  );
}
